import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateNotificationActionUrl } from '../../utils/notificationActionUrl';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  actionUrl?: string;
}

interface ToastContextData {
  addToast: (
    message: string,
    type: Toast['type'],
    duration?: number,
    actionUrl?: string
  ) => void;
  removeToast: (id: number) => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextData>({} as ToastContextData);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((state) => state.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((
    message: string,
    type: Toast['type'],
    duration: number = 3000,
    actionUrl?: string
  ) => {
    const id = Math.random();
    const trimmedActionUrl = actionUrl?.trim();
    const toast: Toast = {
      id,
      message,
      type,
      actionUrl: trimmedActionUrl && trimmedActionUrl.length > 0 ? trimmedActionUrl : undefined,
    };

    setToasts((state) => [...state, toast]);

    setTimeout(() => {
      removeToast(id);
    }, duration);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, toasts }}>
      {children}
    </ToastContext.Provider>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();
  const navigate = useNavigate();

  const handleToastClick = (toast: Toast) => {
    if (!toast.actionUrl)
    {
      return;
    }

    navigateNotificationActionUrl(toast.actionUrl, navigate);
    removeToast(toast.id);
  };

  return (
    <div className="fixed bottom-0 right-0 p-4 space-y-4 z-[99999] bg-transparent pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.actionUrl ? 'button' : undefined}
          tabIndex={toast.actionUrl ? 0 : undefined}
          onClick={() => handleToastClick(toast)}
          onKeyDown={(event) => {
            if (!toast.actionUrl)
            {
              return;
            }

            if (event.key === 'Enter' || event.key === ' ')
            {
              event.preventDefault();
              handleToastClick(toast);
            }
          }}
          className={`p-4 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out pointer-events-auto ${
            toast.type === 'success'
              ? 'bg-green-500'
              : toast.type === 'error'
              ? 'bg-red-500'
              : toast.type === 'warning'
              ? 'bg-yellow-500'
              : 'bg-blue-500'
          } text-white ${toast.actionUrl ? 'cursor-pointer hover:opacity-95' : ''}`}
        >
          <div className="flex items-center justify-between gap-3">
            <p className="flex-1">{toast.message}</p>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                removeToast(toast.id);
              }}
              className="shrink-0 text-white hover:text-gray-200 transition-colors duration-200"
              aria-label="Fechar notificação"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context)
  {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};
